import React, { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SignInState } from "../../../context/auth/signIn/reducer";
import { useFormik } from "formik";
import { cleanSignInErrorMessage, signIn } from "../../../context/auth/action";
import { IToastContext, ToastContext } from "../../../providers/Toast";
import { Link, useHistory } from "react-router-dom";
import * as Yup from "yup";
import CustomInputEmail from "../../../components/shared/forms/input/CustomInputEmail";
import CustomInputPassword from "../../../components/shared/forms/input/CustomInputPassword";
import Button from "../../../components/shared/button/Button";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../routing/routes";
import _environment from "../../../_environment/_environment";
import Cookies from "js-cookie";
import { COOKIES } from "../../../data/enums";
import { IModalContext, ModalContext } from "../../../providers/Modal";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {}

interface FormFields {
  email?: string;
  password?: string;
}

const SignInView = ({ ...restProps }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  // Reference to Redux store dispatch function: use it to dispatch actions.
  const dispatch = useDispatch();

  // Access to the history instance that you may use to navigate.
  let history = useHistory();

  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // Access to toast manager hook that you may use to show toasts.
  const { openToast } = useContext<IToastContext>(ToastContext);

  // Access to modal manager hook that you may use to open and close modals.
  const { openModal, closeModal } = useContext<IModalContext>(ModalContext);

  // -----------------------------------------------------------------
  // f o r m
  // -----------------------------------------------------------------
  // Formik Initialisation data
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email("Invalid email address").required("Email is required!"),
      password: Yup.string().required("Password is required!"),
    }),
    onSubmit: (values: any) => {
      dispatch(signIn(values));
    },
  });

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------
  const { pending, errorMessage } = useSelector(
    ({ signInReducer }: { signInReducer: SignInState }) => {
      return signInReducer;
    }
  );

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------
  const renderForm = (): React.ReactNode => {
    return (
      <form onSubmit={formik.handleSubmit} className="user">
        <div className="form-group">
          <CustomInputEmail
            id="email"
            name="email"
            label={t("forms.email")}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            errors={formik.errors.email}
            touched={formik.touched.email}
          />
        </div>
        <div className="form-group">
          <CustomInputPassword
            id="password"
            name="password"
            label={t("forms.password")}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
            errors={formik.errors.password}
            touched={formik.touched.password}
          />
        </div>
        <div className="text-right mb-3 forgot-password">
          <Link className="link-label" to={ROUTES.FORGOT_PASSWORD}>
            {t("forms.forgot_password")}
          </Link>
        </div>
        <Button
          loading={pending}
          disabled={pending}
          submit={true}
          className="btn btn-primary btn-block"
        >
          {t("forms.sign_in")}
        </Button>
      </form>
    );
  };

  const renderTutorialsModalContent = (): React.ReactNode => {
    return (
      <div className={"dialog signup-tutorials"}>
        <h1>{t("pages.login.modals.tutorial.title")}</h1>
        <br />
        <div className={"row"}>
          <div className="col-xs-12 col-sm-6">
            <h4>{t("forms.preregistration_done")}</h4>
            <p>{t("pages.tutorials.preregistrationDone.description")}</p>
            <br />
            <button
              className={"btn btn-primary"}
              onClick={() => {
                closeModal();
                Cookies.set(COOKIES.SIGNUP_TUTORIALS, "true", { path: "" });
                history.push(ROUTES.PRE_REGISTERED);
              }}
            >
              {t("forms.complete_preregistration")}
            </button>
          </div>
          <div className="col-xs-12 col-sm-6">
            <iframe
              src="https://www.youtube.com/embed/4FEPQq0omVw"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
        <br />
        <hr />
        <br />
        <div className={"row"}>
          <div className="col-xs-12 col-sm-6">
            <h4>{t("forms.not_registered_yet")}</h4>
            <p>{t("pages.tutorials.notRegisteredYet.description")}</p>
            <br />
            <button
              className={"btn btn-primary"}
              onClick={() => {
                closeModal();
                Cookies.set(COOKIES.SIGNUP_TUTORIALS, "true", { path: "" });
                history.push(ROUTES.SIGN_UP);
              }}
            >
              {t("forms.create_new_account")}
            </button>
          </div>
          <div className="col-xs-12 col-sm-6">
            <iframe
              src="https://www.youtube.com/embed/P6tXub27FDk"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    );
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    console.log("[SignInView] init");

    if (!Cookies.get(COOKIES.SIGNUP_TUTORIALS)) {
      // Show signup tutorial modal
      openModal({
        customContent: renderTutorialsModalContent,
        size: "md",
        showCancelButton: false,
        buttonTitle: t("pages.login.modals.tutorial.buttons.confirm"),
        buttonClasses: "btn-default",
        callback: () => () => {
          // Set the cookie to no longer display modal
          Cookies.set(COOKIES.SIGNUP_TUTORIALS, "true", { path: "" });
        },
      });
    }
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    console.log("[SignInView] destroy");
    // destroy all
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  /**
   * Hook to errorMessage in order to handle errors
   */
  useEffect(() => {
    if (errorMessage) {
      // console.log(errorMessage);

      let redirect = false;
      let title = "errors.general.title";
      let type: "error" | "warning" = "error";
      let error = "errors.invalid_credentials";

      if (errorMessage === "EMAIL_NOT_VERIFIED") {
        title = "warnings.general.title";
        redirect = true;
        type = "warning";
        error = "errors.email_not_verified";
      }

      openToast({
        type: type,
        title: title,
        message: error,
        callback: () => {
          dispatch(cleanSignInErrorMessage());
        },
      });

      if (!!redirect) {
        // Redirect to confirm email
        history.push(ROUTES.CONFIRM_EMAIL);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, dispatch, openToast]);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <div className="main-content">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-6 col-lg-7 col-md-9">
            <div className="box o-hidden my-5">
              {/*<div className={"alert alert-warning"}>
                Registrations are temporarily disabled
              </div>*/}
              <div className="logo-vertical-wrapper">
                <a href={_environment.baseURL}>
                  <img src={_environment.assets.logo} alt={t("title")} />
                </a>
              </div>
              <div className="card-body p-0">
                <div className="p-4">
                  <div className="text-center">
                    <h1 className="h4 text-gray-900 mb-4">{t("pages.login.title")}</h1>
                  </div>

                  {renderForm()}
                  <hr />

                  <div className="mt-3 d-flex align-items-center justify-content-between">
                    <div>
                      <label className="mb-2 link-label">{t("forms.not_registered_yet")}</label>
                      <Link className="btn btn-outline btn-primary" to={ROUTES.SIGN_UP}>
                        {t("forms.create_new_account")}
                      </Link>
                    </div>
                    <div className="text-right">
                      <label className="mb-2 link-label">{t("forms.preregistration_done")}</label>
                      <Link className="btn btn-outline btn-primary" to={ROUTES.PRE_REGISTERED}>
                        {t("forms.complete_preregistration")}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default SignInView;
